import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWebsiteAppContext } from '../../hooks/useWebsiteAppContext';
import { formatCurrency } from '../../utils/formatters';
import Icon from '../../../miniapp/ui-kit/Icon';

export default function OrderSuccessScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { orderResult, clearOrderResult } = useWebsiteAppContext();

  useEffect(() => {
    if (!orderResult) {
      navigate('/orders', { replace: true });
    }
  }, [navigate, orderResult]);

  if (!orderResult) {
    return null;
  }

  const handleBackToCatalog = () => {
    clearOrderResult();
    navigate('/');
  };

  const handleViewOrderDetails = () => {
    clearOrderResult();
    navigate('/orders');
  };

  const handleSelectDeliveryTime = () => {
    const orderId = orderResult.id;
    clearOrderResult();
    navigate('/orders', { state: { openDelivery: orderId } });
  };

  return (
    <main className="max-w-[880px] mx-auto px-4 py-8 md:py-12">
      <div className="max-w-2xl mx-auto text-center">
        <div className="mb-8 inline-flex items-center justify-center w-24 h-24 rounded-full bg-secondary text-accent relative">
          <div className="absolute inset-0 rounded-full bg-accent/5 animate-pulse" />
          <Icon name="check_circle" className="!text-5xl !leading-none" />
        </div>

        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-text mb-2">
          {t('success.title')}
        </h1>
        <p className="text-text-secondary font-semibold tracking-wide text-sm mb-10">
          {`${t('success.orderNumber')}: ${orderResult.orderNumber}`}
        </p>

        <div className="grid grid-cols-1 gap-6 text-left">
          <div className="bg-primary p-6 md:p-8 rounded-lg shadow-sm border border-border">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="min-w-0">
                <p className="m-0 text-[11px] font-bold uppercase tracking-widest text-text-secondary">
                  {t('success.orderNumber')}
                </p>
                <p className="m-0 mt-1 text-xl font-bold text-accent break-all">
                  {orderResult.orderNumber}
                </p>
              </div>
              <div className="text-right">
                <p className="m-0 text-[11px] font-bold uppercase tracking-widest text-text-secondary">
                  {t('success.totalAmount')}
                </p>
                <p className="m-0 mt-1 text-xl font-bold text-text">
                  {formatCurrency(orderResult.total)}
                </p>
              </div>
            </div>

            {orderResult.paymentInstruction ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="account_balance" className="text-accent" />
                  <h2 className="m-0 text-lg font-semibold">
                    {t('success.paymentInstructions')}
                  </h2>
                </div>
                <p className="m-0 text-text-secondary leading-relaxed">
                  {orderResult.paymentInstruction}
                </p>
              </>
            ) : (
              <p className="m-0 text-text-secondary leading-relaxed">{t('success.subtitle')}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-secondary p-6 rounded-lg flex items-start gap-4">
              <div className="bg-primary p-3 rounded-md text-accent">
                <Icon name="mail" />
              </div>
              <div>
                <h3 className="m-0 font-semibold">{t('website.success.emailConfirmationTitle')}</h3>
                <p className="m-0 mt-1 text-xs text-text-secondary leading-relaxed">
                  {t('website.success.emailConfirmationDescription')}
                </p>
              </div>
            </div>

            <div className="bg-secondary p-6 rounded-lg flex items-start gap-4">
              <div className="bg-primary p-3 rounded-md text-accent">
                <Icon name="local_shipping" />
              </div>
              <div>
                <h3 className="m-0 font-semibold">{t('website.success.trackProgressTitle')}</h3>
                <p className="m-0 mt-1 text-xs text-text-secondary leading-relaxed">
                  {t('website.success.trackProgressDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 md:mt-14 flex flex-col md:flex-row items-center justify-center gap-4">
          <button
            type="button"
            className="w-full md:w-auto border-0 bg-accent-gradient text-on-accent px-8 py-4 rounded-full font-semibold text-sm shadow-lg shadow-accent/20 cursor-pointer transition-all active:scale-[0.98]"
            onClick={handleSelectDeliveryTime}
          >
            {t('delivery.selectTime')}
          </button>
          <button
            type="button"
            className="w-full md:w-auto border border-border bg-primary text-text px-8 py-4 rounded-full font-semibold text-sm cursor-pointer transition-all active:scale-[0.98]"
            onClick={handleViewOrderDetails}
          >
            {t('website.success.viewOrderDetails')}
          </button>
          <button
            type="button"
            className="w-full md:w-auto border-0 bg-disabled text-on-disabled px-8 py-4 rounded-full font-semibold text-sm cursor-pointer transition-all active:scale-[0.98]"
            onClick={handleBackToCatalog}
          >
            {t('success.backToCatalog')}
          </button>
        </div>
      </div>
    </main>
  );
}
